import { call, put, select, takeEvery } from 'redux-saga/effects';
import { getType } from 'deox';
import {
  addRevision,
  applyNextRevision,
  applyPreviousRevision,
  decrementRevisionOffset,
  getCurrentRevision,
  getNextRevision,
  getPreviousRevision,
  getSnapshot,
  incrementRevisionOffset,
  saveRevision
} from 'modules/revisions';
import { Revision, Snapshot } from 'types/Revision';
import { isNil, omit } from 'ramda';
import {
  getSceneSources,
  initScenesLoader,
  removeSceneSource,
  ScenesLoader
} from 'modules/scenesLoader';
import {
  applyProject,
  changeProjectFileName,
  getCurrentProjectId,
  saveProjectFile
} from 'modules/projectFles';
import { deleteFile, getSrc } from 'utils/fileCache';
import { getCurrentScene, getScenes } from 'modules/scenes';
import { Scene, SceneType } from 'types/Scene';
import { ToolName } from 'types/AppState';
import analytics from 'utils/analytics';
import { getCurrentTextId, setCurrentTextId } from 'modules/currentTextId';
import { getEffects } from 'modules/effects';
import { Effects } from 'types/Effects';
import { getPlayer } from 'utils/PlayerContext';

function* updateCurrentTime() {
  const currScene: Scene = yield select(getCurrentScene);
  getPlayer().setTime(currScene.start);
}

function* updateCurrentTextId() {
  const currentTextId: string[] = yield select(getCurrentTextId);
  const effects: Effects = yield select(getEffects);

  const effectIds = Object.values(effects).flatMap(item => item.map(effect => effect.id));

  if (currentTextId.some(id => !effectIds.includes(id))) {
    yield put(setCurrentTextId([]));
  }
}

function* applyRevision(snapshot: Snapshot | null) {
  if (isNil(snapshot)) {
    return;
  }

  getPlayer().pause();

  const prevScenes: Scene[] = yield select(getScenes);
  const rawPrevScenesSources: ScenesLoader['sources'] = yield select(getSceneSources);
  const prevScenesSources: { sceneId: string; src: string }[] = Object.keys(
    rawPrevScenesSources
  ).map(key => ({ sceneId: key, src: getSrc(rawPrevScenesSources[key]) }));

  const newScenesSources =
    snapshot.scenes?.items.map(item => item.src).filter(item => !isNil(item)) ?? [];

  const sourcesToDownload = newScenesSources.filter(
    item => !prevScenesSources.map(source => source.src).includes(item)
  );

  const getSrcBySceneId = (scenes: Scene[], id: string): string | null =>
    scenes.find(scene => scene.id === id)?.src;

  const newScenesWithContent = snapshot.scenes?.items
    .filter(item => item.type !== SceneType.EMPTY)
    .filter(item => getSrcBySceneId(prevScenes, item.id) !== item.src)
    .map(item => item.id);

  const { projectName, ...projectData } = snapshot;
  const currentProjectId: string = yield select(getCurrentProjectId);

  // @ts-ignore
  yield put(applyProject(projectData));
  yield put(changeProjectFileName(currentProjectId, projectName));

  if (sourcesToDownload.length > 0 || newScenesWithContent.length > 0) {
    yield put(initScenesLoader());
  }

  for (const source of prevScenesSources.filter(item => !newScenesSources.includes(item.src))) {
    yield put(removeSceneSource(source.sceneId));
    yield call(deleteFile, source.src);
  }

  yield updateCurrentTime();
  yield updateCurrentTextId();
  yield put(saveProjectFile());
}

function* saveRevisionSaga({ payload: tool }: ReturnType<typeof saveRevision>) {
  const revision: Revision = yield select(getSnapshot);
  yield put(addRevision({ ...revision, tool }));
  yield put(saveProjectFile());
}

function* applyPreviousRevisionSaga() {
  const revision: Revision | null = yield select(getPreviousRevision);
  const { tool }: Revision = yield select(getCurrentRevision);
  if (revision) {
    yield put(incrementRevisionOffset());
    yield applyRevision(omit(['tool'], revision));

    if (tool !== ToolName.None) {
      analytics.applyRevision({ with_tool: tool, action_button: 'undo' });
    }
  }
}

function* applyNextRevisionSaga() {
  const revision: Revision | null = yield select(getNextRevision);
  if (revision) {
    yield put(decrementRevisionOffset());
    yield applyRevision(omit(['tool'], revision));

    if (revision.tool && revision.tool !== ToolName.None) {
      analytics.applyRevision({ with_tool: revision.tool, action_button: 'redo' });
    }
  }
}

export default function* revisionsSaga() {
  yield takeEvery(getType(saveRevision), saveRevisionSaga);
  yield takeEvery(getType(applyPreviousRevision), applyPreviousRevisionSaga);
  yield takeEvery(getType(applyNextRevision), applyNextRevisionSaga);
}
